/* global SamsungPay */
import React, { useEffect, useState, useRef, useCallback } from "react";
import styles from "./SamsungPayButton.module.css";

// Card brands supported by the Samsung Pay Web SDK, keyed by their settings flag
const SAMSUNG_PAY_BRANDS = {
  allowVisa: "visa",
  allowMastercard: "mastercard",
  allowAmex: "amex",
  allowDiscover: "discover",
  allowMada: "mada",
};

function buildPaymentMethods(settings) {
  return {
    version: "2",
    serviceId: settings.serviceId,
    protocol: settings.protocol,
    allowedBrands: Object.entries(SAMSUNG_PAY_BRANDS)
      .filter(([flag]) => settings[flag])
      .map(([, brand]) => brand),
  };
}

function buildTransactionDetail(settings) {
  return {
    orderNumber: `wts-${Date.now()}`,
    merchant: {
      name: settings.merchantName,
      url: window.location.hostname, // Service domain must match the current page
      id: settings.merchantId,
      countryCode: settings.country,
    },
    amount: {
      option: "FORMAT_TOTAL_PRICE_ONLY",
      currency: settings.currency,
      total: Number(settings.amount),
    },
    type: settings.paymentType,
  };
}

const SamsungPayButton = ({ handleTokenOutput, samsungPaySettings }) => {
  const [canUseSamsungPay, setCanUseSamsungPay] = useState(false);
  const [loading, setLoading] = useState(true);
  const samsungPayButtonRef = useRef(null);
  const buttonCreatedRef = useRef(false);

  // Check Samsung Pay availability for the configured service and brands
  const loadSamsungPay = useCallback(async () => {
    if (!window.SamsungPay) {
      console.log("Samsung Pay not supported");
      setLoading(false);
      return;
    }

    try {
      const paymentClient = new SamsungPay.PaymentClient({
        environment: samsungPaySettings.environment,
      });
      const response = await paymentClient.isReadyToPay(
        buildPaymentMethods(samsungPaySettings),
      );
      setCanUseSamsungPay(response.result);
      if (!response.result) {
        console.log("Samsung Pay not available");
      }
    } catch (error) {
      console.error("Error loading Samsung Pay:", error);
      setCanUseSamsungPay(false);
    } finally {
      setLoading(false);
    }
  }, [samsungPaySettings]);

  const createSamsungPayButton = useCallback(() => {
    const paymentClient = new SamsungPay.PaymentClient({
      environment: samsungPaySettings.environment,
    });
    const paymentMethods = buildPaymentMethods(samsungPaySettings);

    const button = paymentClient.createButton({
      onClick: async () => {
        const transactionDetail = buildTransactionDetail(samsungPaySettings);

        console.log("Payment Methods: ", paymentMethods);
        console.log("Transaction Detail: ", transactionDetail);

        try {
          const paymentCredential = await paymentClient.loadPaymentSheet(
            paymentMethods,
            transactionDetail,
          );
          handleTokenOutput(paymentCredential, "Samsung Pay");

          // Process the payment here, then dismiss the payment sheet
          paymentClient.notify({
            status: "CHARGED", // || CANCELED || REJECTED || ERRED
            provider: "Checkout.com",
          });
        } catch (error) {
          console.error("Error processing Samsung Pay request:", error);
        }
      },
      buttonStyle: samsungPaySettings.buttonStyle,
    });

    // Clear previous button if exists
    if (samsungPayButtonRef.current) {
      samsungPayButtonRef.current.innerHTML = "";
      samsungPayButtonRef.current.appendChild(button);
      buttonCreatedRef.current = true; // Prevent further button creations
    }
  }, [samsungPaySettings, handleTokenOutput]);

  // Check Samsung Pay availability on load
  useEffect(() => {
    loadSamsungPay();
  }, [loadSamsungPay]);

  useEffect(() => {
    if (canUseSamsungPay && !buttonCreatedRef.current) {
      createSamsungPayButton();
    }
  }, [canUseSamsungPay, createSamsungPayButton]);

  // Watch for changes in samsungPaySettings to recreate the button
  useEffect(() => {
    if (canUseSamsungPay && buttonCreatedRef.current) {
      createSamsungPayButton(); // Recreate button on setting change
      buttonCreatedRef.current = false; // Reset to allow re-creation
    }
  }, [samsungPaySettings, canUseSamsungPay, createSamsungPayButton]);

  return (
    <>
      {loading ? (
        <div
          style={{
            marginBottom: "20px",
            width: "240px",
            height: "40px",
          }}
        ></div>
      ) : canUseSamsungPay ? (
        <div
          ref={samsungPayButtonRef}
          id="samsungpay-container"
          className={styles.samsungPayButton}
          style={{
            marginBottom: "20px",
            width: "240px",
            height: "40px",
          }}
        >
          {/* Samsung Pay button will be inserted here */}
        </div>
      ) : (
        <div></div>
      )}
    </>
  );
};

export default SamsungPayButton;
